import React, { useState, useEffect, useContext, useRef } from "react";

import {
  Button,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faIdBadge } from "@fortawesome/free-solid-svg-icons";
import { DataContext } from "../../../context/DataContext";
import { isAccess, simpleMessage } from "../../../helpers/Helpers";
import { getToken } from "../../../api/Account";
import { getPuestosAsync } from "../../../api/Employees";
import {
  CargandoTabla,
  EstadoError,
  EstadoVacio,
  PageHeader,
} from "../../../components/base";
import { ERROR_OTRA_SESION, encabezadoTabla } from "../nomina/comunes";
import AddPuesto from "./AddPuesto";
import EditPuesto from "./EditPuesto";

const FILAS_POR_PAGINA = 10;

const CargoList = () => {
  const [cargoList, setCargoList] = useState([]);
  const [selectedCargo, setSelectedCargo] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [localReload, setLocalReload] = useState(false);

  // Estado local de la lista: esqueleto mientras carga y error con "Reintentar".
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const peticionActual = useRef(0);

  const { access, setIsLoading, setIsUnautorized } = useContext(DataContext);

  const token = getToken();

  const cargar = async () => {
    const peticion = ++peticionActual.current;
    setCargando(true);
    setError(false);
    setIsLoading(true);
    try {
      const result = await getPuestosAsync(token);
      if (peticion !== peticionActual.current) return;

      if (!result.statusResponse) {
        setIsLoading(false);
        setCargando(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        setError(true);
        if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
        }
        return;
      }

      setIsLoading(false);
      setCargoList(result.data.result);
      setCargando(false);
    } catch (e) {
      // Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
      if (peticion !== peticionActual.current) return;
      setIsLoading(false);
      setCargando(false);
      setError("conexion");
    }
  };

  useEffect(() => {
    cargar();
  }, [localReload]);

  const [pagina, setPagina] = useState(0);

  // Si tras recargar la página actual queda fuera de rango, se vuelve a la primera.
  useEffect(() => {
    if (pagina > 0 && pagina * FILAS_POR_PAGINA >= cargoList.length) {
      setPagina(0);
    }
  }, [pagina, cargoList.length]);

  const currentItem = cargoList.slice(
    pagina * FILAS_POR_PAGINA,
    (pagina + 1) * FILAS_POR_PAGINA
  );

  // Mismos permisos que antes: agregar con NOMINA CREAR y editar con NOMINA UPDATE.
  const puedeAgregar = isAccess(access, "NOMINA CREAR");
  const puedeEditar = isAccess(access, "NOMINA UPDATE");

  const botonAgregar = (
    <Button
      onClick={() => {
        setShowModal(true);
      }}
      startIcon={<AddIcon />}
      variant="contained"
    >
      Agregar puesto
    </Button>
  );

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={6} columnas={3} etiqueta="Cargando puestos…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar los puestos"
        }
        onReintentar={cargar}
      />
    );
  } else if (cargoList.length === 0) {
    contenido = (
      <EstadoVacio
        titulo="Todavía no hay puestos"
        descripcion="Aquí aparecen los puestos de trabajo que se asignan a los empleados. Agrega el primero."
        accion={puedeAgregar ? botonAgregar : undefined}
      />
    );
  } else {
    contenido = (
      <>
        <TableContainer>
          <Table aria-label="Lista de puestos">
            <TableHead>
              <TableRow>
                <TableCell sx={encabezadoTabla}>#</TableCell>
                <TableCell sx={encabezadoTabla}>Puesto</TableCell>
                {puedeEditar ? (
                  <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                    Acciones
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentItem.map((row) => {
                const { id, description } = row;
                return (
                  <TableRow key={id} hover>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ fontVariantNumeric: "tabular-nums", width: 80 }}
                    >
                      {id}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {description}
                      </Typography>
                    </TableCell>
                    {puedeEditar ? (
                      <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <Tooltip title="Editar puesto">
                          <IconButton
                            aria-label={`Editar el puesto ${description}`}
                            onClick={() => {
                              setSelectedCargo(row);
                              setShowEditModal(true);
                            }}
                            sx={{ color: "primary.main" }}
                          >
                            <EditOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={cargoList.length}
          page={pagina}
          onPageChange={(e, nueva) => setPagina(nueva)}
          rowsPerPage={FILAS_POR_PAGINA}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          getItemAriaLabel={(tipo) =>
            ({
              first: "Primera página",
              last: "Última página",
              next: "Página siguiente",
              previous: "Página anterior",
            }[tipo])
          }
        />
      </>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Puestos"
        subtitulo="Puestos de trabajo que se asignan a los empleados"
        icono={<FontAwesomeIcon icon={faIdBadge} />}
        acciones={puedeAgregar ? botonAgregar : undefined}
      />

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>{contenido}</Paper>

      <AddPuesto
        abierto={showModal}
        setShowModal={setShowModal}
        puestoReload={localReload}
        setPuestoReload={setLocalReload}
      />

      <EditPuesto
        abierto={showEditModal}
        selected={selectedCargo}
        setShowModal={setShowEditModal}
        localReload={localReload}
        setLocalReload={setLocalReload}
      />
    </Container>
  );
};

export default CargoList;
